import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { BookingPatterns as BookingPatternsData } from '@theraply/api-client';
import { ClockIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export interface BookingPatternsProps {
  data: BookingPatternsData;
  loading?: boolean;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1'];

/**
 * Booking Patterns Component
 * Displays booking trends by day of week and time of day
 */
export function BookingPatterns({ data, loading = false }: BookingPatternsProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="h-80 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.dayOfWeekLabels || data.dayOfWeekLabels.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Booking Patterns</h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <CalendarDaysIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p className="text-lg">No booking data available</p>
            <p className="text-sm mt-2">Booking patterns will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for day of week chart
  const dayOfWeekData = data.dayOfWeekLabels
    .map((day) => ({
      day: DAY_NAMES[day.dayOfWeek],
      count: day.count,
      dayOfWeek: day.dayOfWeek,
    }))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek);

  // Find peak hours
  const peakHours = data.peakHours || [];

  // Calculate most popular day
  const mostPopularDay = dayOfWeekData.reduce((max, day) =>
    day.count > max.count ? day : max
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Booking Patterns</h3>

      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
          <div className="flex items-start">
            <CalendarDaysIcon className="h-6 w-6 text-indigo-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700">Most Popular Day</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">
                {mostPopularDay.day}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {mostPopularDay.count} bookings
              </p>
            </div>
          </div>
        </div>

        {peakHours.length > 0 && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <div className="flex items-start">
              <ClockIcon className="h-6 w-6 text-purple-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-700">Peak Booking Time</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {peakHours[0].timeRange}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {peakHours[0].count} bookings
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Day of Week Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-4">Bookings by Day of Week</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={dayOfWeekData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="day"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '12px',
              }}
              labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
            />
            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
              {dayOfWeekData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Heatmap (simplified view) */}
      {data.heatmapData && data.heatmapData.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            Booking Heatmap (Day × Hour)
          </h4>
          <div className="overflow-x-auto">
            <HeatmapGrid data={data.heatmapData} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simplified heatmap grid component
 */
function HeatmapGrid({ data }: { data: BookingPatternsData['heatmapData'] }) {
  // Group data by day and hour
  const grid: Record<number, Record<number, number>> = {};

  data.forEach((point) => {
    if (!grid[point.dayOfWeek]) {
      grid[point.dayOfWeek] = {};
    }
    grid[point.dayOfWeek][point.hourOfDay] = point.bookingCount;
  });

  // Find max for color scaling
  const maxCount = Math.max(...data.map((d) => d.bookingCount), 1);

  // Get all unique hours
  const hours = Array.from(
    new Set(data.map((d) => d.hourOfDay))
  ).sort((a, b) => a - b);

  if (hours.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No heatmap data available
      </div>
    );
  }

  return (
    <div className="inline-block min-w-full">
      <div className="grid gap-1" style={{ gridTemplateColumns: `auto repeat(${hours.length}, 1fr)` }}>
        {/* Header row */}
        <div className="p-2"></div>
        {hours.map((hour) => (
          <div
            key={hour}
            className="p-2 text-xs text-center text-gray-600 font-medium"
          >
            {hour}h
          </div>
        ))}

        {/* Data rows */}
        {[0, 1, 2, 3, 4, 5, 6].map((day) => (
          <React.Fragment key={day}>
            <div className="p-2 text-xs text-gray-600 font-medium">
              {DAY_NAMES[day]}
            </div>
            {hours.map((hour) => {
              const count = grid[day]?.[hour] || 0;
              const intensity = count / maxCount;
              return (
                <div
                  key={`${day}-${hour}`}
                  className={clsx(
                    'p-2 rounded text-xs text-center transition-all hover:scale-105',
                    count === 0
                      ? 'bg-gray-100 text-gray-400'
                      : 'text-white cursor-pointer'
                  )}
                  style={{
                    backgroundColor:
                      count > 0
                        ? `rgba(99, 102, 241, ${0.3 + intensity * 0.7})`
                        : undefined,
                  }}
                  title={`${DAY_NAMES[day]} ${hour}:00 - ${count} bookings`}
                >
                  {count > 0 ? count : ''}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default BookingPatterns;
